/**
 * The camera background-effect lifecycle for `LiveKitRoomConnector` (SC-158).
 *
 * Lifted out of the connector so it can be driven by tests with a fake Room,
 * track and processor. The connector owns the refs and passes them in; this
 * module owns the rules.
 *
 * **The rule everything else serves: with an effect selected, the raw camera is
 * never shown or published, and nothing but the selection is.** Someone who
 * picked a background did it to hide the room behind them. So:
 *
 *  - **Only an explicit "none" brings the raw camera back.** No failure, no slow
 *    device and no missing image falls back to it, and none clears the saved
 *    choice. The user is told what went wrong and decides.
 *  - **A camera starts only with a ready processor.** With an effect selected,
 *    the connector asks {@link VideoBackgroundLifecycle.prepareCameraStart}
 *    before every `setCameraEnabled(true)`; it says yes once a processor for the
 *    effect is parked in the Room's `videoCaptureDefaults`, so LiveKit attaches
 *    it inside `createLocalTracks` and the first frame out is composited.
 *  - **Held, not raw.** While a live camera waits for its effect — the first
 *    effect turned on mid-call, a just-started camera before its first
 *    composited frame — it is *held*: `pauseUpstream()` takes it off the wire
 *    (remote participants see the camera-off state), the patched library keeps
 *    LiveKit from resuming it on its own (a guard re-pauses should one get
 *    through), and the local self-view covers itself. It is released on the
 *    first processed frame.
 *  - **A camera that cannot get its effect is taken down.** A held camera with
 *    no working processor would go out raw on the next LiveKit restart (unmute,
 *    device switch, reconnect), so it is unpublished instead, and the next start
 *    goes through the gate.
 *  - **Effect to effect keeps the old one on screen.** The new image is fetched
 *    and decoded while the old effect stays live, then `switchTo` swaps it on
 *    the same processor — atomically, with the library patched for it — never
 *    `setProcessor` on a track that already has one, which puts the raw camera
 *    on the sender during the swap. If the new pick fails, the old effect stays
 *    and stays selected — including the one a starting camera is parked with.
 *  - **A slow device steps down once, silently.** Segmentation starts on the
 *    heavier default model. The first time the health check calls the pipeline
 *    too slow on a page, or merely busy for about 5 s on end, the camera is
 *    rebuilt on the lighter model (a camera restart, through the gate, like any
 *    other that has to replace a processor); a second slow verdict is the "slow"
 *    notice, as before, and a busy one is never a notice. The lighter model is
 *    fetched when a busy streak begins, so the rebuild does not wait on a cold
 *    download. See {@link stepDownSegmentation}.
 *  - **Nothing waits for ever.** A processor start is bounded — on a live
 *    camera and inside a camera start alike — and given up on for a newer pick
 *    (a camera start, for None), so a hung model load can neither keep the
 *    camera held nor keep a None, a camera start or a device switch waiting
 *    behind it. A processor given up on is destroyed once its load settles.
 *  - **Latest wins.** Applies are serialised, and whatever the user picked last
 *    is what ends up on the track, in the park and in the announcement.
 *
 * Screen share is never touched: every entry point that sees a publication
 * checks for `Track.Source.Camera` first, and the track lookup is the
 * connector's camera-only `findLocalCameraTrack`.
 */
import {
  ConnectionState,
  Track,
  TrackEvent,
  type LocalTrackPublication,
  type LocalVideoTrack,
  type Room,
} from 'livekit-client'
import type { BackgroundProcessorWrapper } from '@livekit/track-processors'

import {
  BACKGROUND_EFFECT_VIDEO_PRESET,
  CAPTURE_FRAME_RATE_TOLERANCE_FPS,
  VIDEO_PRESETS,
  meetsEffectCapture,
  type VideoConstraints,
} from '../config/mediaConfig'
import {
  readCameraWithoutBackgroundOnThisDevice,
  saveBackgroundEffectPreference,
} from '../services/backgroundEffectPreference'
import {
  NO_BACKGROUND_EFFECT,
  PROCESSOR_START_TIMEOUT_MS,
  backgroundEffectKey,
  backgroundEffectsEqual,
  backgroundProblemHoldsCamera,
  backgroundProblemsEqual,
  createBackgroundProcessor,
  createSlowStreakTracker,
  destroyBackgroundProcessor,
  evaluateBackgroundHealth,
  isBackgroundEffectAvailable,
  isProcessorOnSteppedDownModel,
  preloadBackgroundImage,
  prewarmBackgroundAssets,
  prewarmFallbackSegmentationModel,
  requiresBackgroundImage,
  resolveBackgroundImage,
  stepDownSegmentation,
  toProcessorOptions,
  type BackgroundEffect,
  type BackgroundFrameStats,
  type BackgroundProblem,
  type BackgroundProblemCause,
  type CameraBackgroundState,
  type SlowStreakTracker,
} from '../services/liveKitVideoBackground'
import { backgroundProblemMessage } from '../services/backgroundProblemCopy'
import { toMediaTrackConstraints } from '../services/livekitNetworkResilience'
import { destroyProcessorAndLateInit } from '../services/processorStartup'
import {
  isParkedProcessorReusable,
  shouldAdoptCaptureTimeProcessor,
  shouldParkCaptureProcessor,
} from './LiveKitRoomConnectorUtils'

/**
 * How long the join waits for the chosen effect to be parked before connecting.
 *
 * The connect never needs the park — the camera-start gate waits for it — so
 * this only gives a download a head start before the camera is asked for.
 * Bounded, because audio and the join itself must never wait on an image.
 */
export const CONNECT_PARK_TIMEOUT_MS = 1500

/**
 * How long a processor that has started may take to produce its first frame
 * before it counts as failed. The clock starts once `setProcessor` has resolved
 * (or, for a capture-time processor, once the track has its sender) — after the
 * WASM and model have loaded, which {@link PROCESSOR_START_TIMEOUT_MS} bounds —
 * so this covers only the pipeline's warm-up: the first segmentation and the
 * first composite.
 */
export const FIRST_FRAME_TIMEOUT_MS = 10_000

/** A wait that a newer apply request cut short. */
const SUPERSEDED = Symbol('superseded')

/**
 * The property the patched `LocalTrack.resumeUpstream` reads
 * (`client/patches/livekit-client+2.18.1.patch`). Set on a held camera, it
 * turns LiveKit's own resumes (the end of every restart, a source unmute) into
 * no-ops, so the camera stays off the wire until the hold ends: before, each
 * one sent it, black, until the guard could pause again. Only the hold sets and
 * clears it.
 */
export const UPSTREAM_HELD_FLAG = 'sevvUpstreamHeld'

const setUpstreamHeld = (track: LocalVideoTrack, held: boolean): void => {
  ;(track as unknown as Record<string, unknown>)[UPSTREAM_HELD_FLAG] = held
}

// ---------------------------------------------------------------------------
// Our own camera's picture while a None-to-effect switch holds it (Safari PiP)
// ---------------------------------------------------------------------------

/**
 * How long the hold preview outlives its release. Picture-in-picture swaps to the processed track on the
 * model the release produces, a render later; stopped at once, the preview would end under it first and
 * leave a frozen or black frame for that moment.
 */
export const HOLD_PREVIEW_HANDOVER_MS = 500

/**
 * The user's decision of 2026-10-08: no picture-in-picture should pause the video. When someone with a live
 * raw camera ("No effect") picks an effect mid-call, the hold switches that camera's source off until the
 * effect's first frame, and the track's `mediaStreamTrack` is the processor's output from then on - so
 * Safari's single-video window, which may be showing that camera, has no live picture of it. This is a
 * clone of the raw source taken just before it is switched off, for that window and nothing else: never
 * published, never given to a sender, never attached anywhere but Safari's PiP video. Others still get
 * nothing until the release, exactly as before; the raw picture was already what this user saw of
 * themselves.
 *
 * Only that switch. A camera starting with an effect, a reconnect or a failure has no raw picture that was
 * the current view, so there is nothing to keep.
 */
let holdPreview: MediaStreamTrack | null = null
const holdPreviewListeners = new Set<() => void>()

/** Our own live camera picture while a None-to-effect switch holds it, or `null`. Local display only. */
export function readLocalCameraHoldPreview(): MediaStreamTrack | null {
  return holdPreview
}

export function subscribeLocalCameraHoldPreview(listener: () => void): () => void {
  holdPreviewListeners.add(listener)
  return () => {
    holdPreviewListeners.delete(listener)
  }
}

function setHoldPreview(next: MediaStreamTrack | null): void {
  if (holdPreview === next) return
  holdPreview = next
  holdPreviewListeners.forEach((listener) => listener())
}

export interface ParkedCaptureProcessor {
  processor: BackgroundProcessorWrapper
  /** The effect it was built for: what a camera starting now comes up with. */
  effect: BackgroundEffect
  effectKey: string
  /**
   * The Room whose capture defaults hold it. LiveKit copies
   * `videoCaptureDefaults` per Room, so a processor parked in one Room's
   * defaults does nothing for the next Room's camera.
   */
  room: Room
  /** The image it was built with, so a failed switch away from it can switch back. */
  imageUrl?: string | null
}

export type BackgroundNotification = {
  type: 'info' | 'warning'
  message: string
  duration: number
}

/**
 * Whether a park left the effect ready. Not a discriminated union: this
 * project compiles without strictNullChecks, where `ok` would not narrow it.
 */
export interface ParkResult {
  ok: boolean
  /** Why not, when `ok` is false. */
  cause?: BackgroundProblemCause
}

export interface ApplyOptions {
  /**
   * Save the choice as the user's preference once it is the selection. False
   * for picks that are not the user's own here: the durable preference arriving
   * from another device, a re-apply after a publish, or the pre-join dialog,
   * which saves for itself.
   */
  persist?: boolean
}

export interface VideoBackgroundLifecycleDeps {
  /** The effect the user wants. Survives the camera being off and every republish. */
  effectRef: { current: BackgroundEffect }
  /** The processor attached to the currently published camera track. */
  processorRef: { current: BackgroundProcessorWrapper | null }
  parkedRef: { current: ParkedCaptureProcessor | null }
  frameStatsRef: { current: BackgroundFrameStats[] }
  /** Camera-only by contract, so screen share can never be processed. */
  findLocalCameraTrack: (room: Room) => LocalVideoTrack | null
  /** The connector's per-frame side effects; see the connector for why. */
  makeFrameProcessedHandler: (room: Room) => (stats: BackgroundFrameStats) => void
  beginVideoToggleCooldown: () => void
  /** Window-event bridge to the pickers: what the call is actually showing. */
  announceEffect: (effect: BackgroundEffect, reverted: boolean) => void
  notify: (notification: BackgroundNotification) => void
  /** For the camera button's warning icon and the local self-view's cover. */
  publishState: (state: CameraBackgroundState) => void
  /**
   * Stop and unpublish the camera without the connector's "camera stopped
   * unexpectedly" auto-republish, which would start it again past the gate.
   */
  unpublishCamera: (room: Room, track: LocalVideoTrack) => Promise<void>
  /**
   * The effect is ready, or no longer wanted: start a camera the user wants on
   * but the gate kept off. The connector checks the user's camera preference.
   */
  requestCameraStart: (room: Room) => void
  /**
   * The capture the network profile chooses with no effect on, which a camera
   * goes back to when the effect is turned off. Left out, the capture stays as
   * it is. [SC-478]
   */
  noEffectCapture?: () => VideoConstraints
}

export interface VideoBackgroundLifecycle {
  applyVideoBackground: (
    room: Room | null,
    effect: BackgroundEffect,
    options?: ApplyOptions
  ) => Promise<void>
  parkCaptureProcessor: (room: Room | null, effect: BackgroundEffect) => Promise<ParkResult>
  /** The join's park: never holds `room.connect` for longer than the bound. */
  parkBeforeConnect: (room: Room, effect: BackgroundEffect, timeoutMs?: number) => Promise<void>
  /**
   * The camera-start gate. Every `setCameraEnabled(true)` asks first; `false`
   * means the camera must stay off for now, and the user has been told why.
   */
  prepareCameraStart: (room: Room) => Promise<boolean>
  /**
   * A camera start the gate allowed has failed. When the parked processor was
   * consumed by it, or its start took too long, the effect would not start —
   * say so rather than leave a console line. When it was given up on for a
   * newer choice, start the camera again with that one.
   *
   * True when this explained the failure, or is starting the camera again: the
   * caller has nothing to add about the device.
   */
  handleCameraStartFailed: (room: Room) => boolean
  /**
   * A camera start the user turned off while it waited has failed: there is
   * nothing to tell anyone, but nothing of it may carry over into the next
   * start or the next real failure. Quiet: no problem, notice or restart.
   */
  forgetCameraStart: (room: Room) => void
  detachVideoBackground: (room: Room | null) => Promise<boolean>
  handleLocalSenderCreated: (room: Room, sender: RTCRtpSender, track: unknown) => Promise<void>
  handleLocalTrackPublished: (room: Room, publication: LocalTrackPublication) => Promise<void>
  handleCameraUnmuted: (room: Room, publication: LocalTrackPublication) => Promise<void>
  /**
   * A camera track went away, by whatever path: nothing is held for it any more, and a new camera track
   * may start in `room`, so the effect is parked for it.
   */
  handleCameraUnpublished: (room: Room, publication: LocalTrackPublication) => void
  /** Settles once no apply is in flight. A camera restart must not race one. */
  whenSettled: () => Promise<void>
  /**
   * How many requests have changed the effect, saved or not: the Join screen's
   * pick is applied unsaved. A request for the effect already selected (a
   * publish or unmute re-asking) is not a pick.
   */
  pickCount: () => number
  /** The Room was disconnected, replaced, or its connect was cancelled. */
  retireRoom: (room: Room | null | undefined) => void
  /**
   * Someone else is signed in now. What went wrong for the last user's
   * background is not theirs, and the page outlives a logout.
   */
  forgetUser: () => void
}

/**
 * Let the durable cross-device preference override the cached one the ref was
 * seeded with, once it has been read.
 *
 * A pick made while the read was out wins: the user chose it here and now, and
 * the stored value is what they chose before. Without that check a late answer
 * put the old choice back on screen and in the cache, and could cancel the pick
 * before it was ever saved. Picks are counted, not compared, so picks that end
 * on the starting effect win too; a change of effect that is not a pick still
 * stops the read. A failed or empty read changes nothing.
 *
 * SC-452: an answer is applied only if the lookup ran for the Parse user signed
 * in when it lands; otherwise it is dropped, so it never reaches someone else's
 * device copy. A read that outlives a sign-out and sign-in of the same user still lands.
 */
export async function restoreDurableBackgroundEffect(deps: {
  effectRef: { current: BackgroundEffect }
  /** The stored effect and the user whose session read it, or null. */
  load: () => Promise<{ effect: BackgroundEffect; userId: string } | null>
  isCancelled: () => boolean
  /** The lifecycle's `pickCount`. */
  pickCount: () => number
  /**
   * The signed-in Parse user's id, or null. Not the store's: it lags Parse both
   * ways, and the device copy written below is the Parse user's.
   */
  currentUserId: () => string | null
  writeCache: (effect: BackgroundEffect) => void
  announce: (effect: BackgroundEffect) => void
  apply: (effect: BackgroundEffect) => void
}): Promise<void> {
  const startEffect = deps.effectRef.current
  const startPicks = deps.pickCount()
  const read = await deps.load()
  if (deps.isCancelled() || !read) return
  if (read.userId !== deps.currentUserId()) return
  const durable = read.effect
  if (deps.pickCount() !== startPicks) return
  if (!backgroundEffectsEqual(deps.effectRef.current, startEffect)) return
  if (backgroundEffectsEqual(durable, deps.effectRef.current)) return

  deps.effectRef.current = durable
  deps.writeCache(durable)
  deps.announce(durable)
  deps.apply(durable)
}

/**
 * SC-452: someone else is signed in now, or nobody. The effect becomes their
 * device copy (`none` for nobody, or for no copy) before their saved choice is
 * read. Otherwise the last person's effect is what the next one sees while that
 * read is out, after it fails, and for good when they never saved a choice. It
 * has to come first: the read takes the effect it starts from as "not a pick
 * made here", and would otherwise keep this switch from being overridden.
 */
export function switchToUsersBackgroundEffect(deps: {
  effectRef: { current: BackgroundEffect }
  deviceCopy: BackgroundEffect
  announce: (effect: BackgroundEffect) => void
  apply: (effect: BackgroundEffect) => void
}): void {
  if (backgroundEffectsEqual(deps.effectRef.current, deps.deviceCopy)) return
  deps.effectRef.current = deps.deviceCopy
  deps.announce(deps.deviceCopy)
  deps.apply(deps.deviceCopy)
}

type CaptureDefaults = { processor?: unknown }

function captureDefaultsOf(room: Room): CaptureDefaults {
  const options = room.options as { videoCaptureDefaults?: CaptureDefaults } | undefined
  return options?.videoCaptureDefaults ?? {}
}

/**
 * Whether LiveKit has already started this processor on a track.
 *
 * Such a processor belongs to its track — LiveKit destroys it when the track
 * stops — and handing it to another camera start would publish a destroyed
 * pipeline.
 *
 * `lifecycleState` leaves `'idle'` the moment `init()` begins, and that is the
 * mark that matters: `processedTrack` is only assigned at the *end* of
 * `init()`, after the model and WASM have loaded, while `setup()` has created
 * the canvases at the start. Destroying a processor in that window released
 * the WebGL context LiveKit was about to render with, and the library never
 * gets it back. `processedTrack` stays as a second mark for builds that do not
 * expose the state.
 */
function hasProcessorStarted(processor: BackgroundProcessorWrapper): boolean {
  const state = (processor as unknown as { lifecycleState?: unknown }).lifecycleState
  if (typeof state === 'string' && state !== 'idle') return true
  return (processor as { processedTrack?: unknown }).processedTrack != null
}

/** A publication's track as a camera track, or null. Duck-typed so fakes can stand in. */
function asCameraTrack(track: unknown): LocalVideoTrack | null {
  const candidate = track as Partial<LocalVideoTrack> | null | undefined
  return candidate && typeof candidate.getProcessor === 'function'
    ? (candidate as LocalVideoTrack)
    : null
}

function attachedProcessorOf(track: LocalVideoTrack): BackgroundProcessorWrapper | null {
  return (track.getProcessor?.() ?? null) as BackgroundProcessorWrapper | null
}

/**
 * Attach the track's video elements again, so they play. Call only once
 * `stopProcessor()` has resolved, because the swap back to the raw camera happens
 * inside it.
 *
 * LiveKit swaps the raw camera into the element's existing MediaStream in place
 * and calls no `play()` (and Safari video gets no `autoplay`). So Safari left the
 * user's own tile paused, blank, until a visibility change made LiveKit `play()`
 * it. `attach` always calls `play()`, and on the same element it keeps the same
 * MediaStream. `srcObject` is left alone on purpose: clearing it made `attach`
 * build a new stream, which reloads, and can blank, the tile on every browser.
 */
function reattachVideoElements(track: LocalVideoTrack): void {
  for (const element of [...(track.attachedElements ?? [])]) {
    if (!(element instanceof HTMLVideoElement)) continue
    try {
      track.attach(element)
    } catch {
      /* LiveKit's visibility handler still plays it on the next window switch */
    }
  }
}

/**
 * Bound on changing the capture of a live camera, for the raise and the restore
 * alike. Short, because both run inside the apply every later pick queues behind
 * (the raise also while the camera is held), and a camera that never answers
 * must not hold up the next effect, nor every camera start behind `whenSettled`.
 */
export const CAPTURE_CHANGE_TIMEOUT_MS = 3_000

/** What a constraint asks for: a bare number or an `ideal`, or an `exact`. */
function askedFor(constraint: unknown): number | undefined {
  if (typeof constraint === 'number') return constraint
  if (constraint && typeof constraint === 'object') {
    const { ideal, exact } = constraint as { ideal?: unknown; exact?: unknown }
    if (typeof exact === 'number') return exact
    if (typeof ideal === 'number') return ideal
  }
  return undefined
}

/**
 * Whether the constraints LiveKit restarts the camera from already ask for the
 * effect capture: at least its height, and no more than its frame rate. The live
 * settings cannot say: they read the old values until an `applyConstraints`
 * settles, so after a restore that timed out or was refused they still show the
 * effect capture while a restart would open the camera at the ordinary one.
 */
function restartsAtEffectCapture(track: LocalVideoTrack, target: VideoConstraints): boolean {
  const restart = track.constraints as MediaTrackConstraints | undefined
  const height = askedFor(restart?.height)
  const frameRate = askedFor(restart?.frameRate)
  return (
    height !== undefined &&
    height >= target.height &&
    frameRate !== undefined &&
    frameRate <= target.frameRate
  )
}

/**
 * Write what a camera restarts from. Returns the way back, which puts the
 * values this write replaced back, but only those this write still owns: a newer
 * write to the same camera has its own camera answer to wait for.
 */
function setRestartConstraints(
  track: LocalVideoTrack,
  constraints: MediaTrackConstraints
): () => void {
  const restart = track.constraints as Record<string, unknown> | undefined
  if (!restart || typeof restart !== 'object') return () => undefined
  const written = constraints as Record<string, unknown>
  const before = { ...restart }
  Object.assign(restart, written)
  return () => {
    for (const key of Object.keys(written)) {
      if (restart[key] !== written[key]) continue
      if (key in before) restart[key] = before[key]
      else delete restart[key]
    }
  }
}

/**
 * Write capture constraints to a live camera, both places that hold them.
 *
 * `applyConstraints` changes the running track, and `track.constraints` is what
 * LiveKit restarts the camera from on every unmute, so either write alone is
 * undone. Only valid while `mediaStreamTrack` is the camera itself, that is with
 * no processor attached: a processor's output track ignores constraints.
 *
 * Written before the camera is asked, so a restart in the meantime already
 * opens at the new capture. If the camera refuses and `rollBackIfRefused` is
 * set, the restart constraints go back to match the camera, which did not change.
 * A camera that is not running (muted) is not asked at all: see below.
 */
async function writeCaptureConstraints(
  track: LocalVideoTrack,
  constraints: MediaTrackConstraints,
  rollBackIfRefused: boolean
): Promise<void> {
  const rollBack = setRestartConstraints(track, constraints)
  const source = track.mediaStreamTrack
  if (!source || typeof source.applyConstraints !== 'function') return
  // A muted camera has no running source: LiveKit stops it on mute and reopens it
  // from these constraints on unmute. Chrome refuses `applyConstraints` on such a
  // track, and the rollback below would then put back the capture this write
  // replaced, so the next unmute would open at the wrong one. Written, and done.
  if (source.readyState !== 'live') return
  try {
    await source.applyConstraints(constraints)
  } catch (error) {
    if (rollBackIfRefused) rollBack()
    throw error
  }
}

/**
 * Change a live camera's capture, waiting for it only so long. Throws if the
 * camera refuses; a camera that does not answer in time is left to answer late,
 * with the restart constraints already written, and is not an error.
 */
async function changeLiveCapture(
  track: LocalVideoTrack,
  capture: VideoConstraints,
  rollBackIfRefused: boolean
): Promise<void> {
  let timer: ReturnType<typeof setTimeout> | undefined
  try {
    await Promise.race([
      writeCaptureConstraints(track, toMediaTrackConstraints(capture), rollBackIfRefused),
      new Promise<void>((resolve) => {
        timer = setTimeout(resolve, CAPTURE_CHANGE_TIMEOUT_MS)
      }),
    ])
  } finally {
    clearTimeout(timer)
  }
}

/**
 * Cameras whose capture this module raised to the effect's and has not put back.
 *
 * The raise runs before the processor is attached, so a "No effect" picked while
 * it is still answering finds a camera at the effect capture with no processor
 * to take off. This is how that None still knows the capture is ours to restore,
 * while a camera it never raised (the adaptive tier, a screen-share reduction) is
 * left alone. A WeakSet: it says nothing about a track once the track is gone.
 */
const raisedForEffect = new WeakSet<LocalVideoTrack>()

/**
 * Raise a live camera to the effect's capture resolution and frame rate.
 *
 * The 720p capture is otherwise only right for someone who joined with an
 * effect already on. Once a camera publication exists, `setCameraEnabled`
 * unmutes it and ignores the capture options passed in, so turning the first
 * effect on mid-call left a 360p track - the resolution at which the library's
 * fixed mask feather erases the gaps between fingers.
 *
 * Skipped only when the camera is there and what it restarts from asks for it
 * too ({@link restartsAtEffectCapture}). A restore still waiting on its camera
 * has already written the ordinary capture to those constraints, so it never
 * reads as "there already": this raise asks the camera again, and a browser
 * serves the requests for one track in the order they were made, so the effect
 * capture lands last. A refusal keeps the restart constraints asking for the
 * effect capture, since a restart under the effect must never open below it
 * (SC-158) and, unlike this apply, a restart always settles for the best match.
 *
 * Called before a processor is attached, so `mediaStreamTrack` is still the
 * camera itself. Turning the effect off goes back down through
 * {@link restoreCaptureAfterEffect}, for a camera marked here ({@link raisedForEffect}).
 * Best-effort and bounded throughout: a camera that will not go higher keeps what
 * it has.
 */
async function raiseCaptureResolutionForEffect(track: LocalVideoTrack): Promise<void> {
  const target = VIDEO_PRESETS[BACKGROUND_EFFECT_VIDEO_PRESET]
  try {
    // A 720p camera still running 30 fps (an old restart, a camera that picked
    // its own rate) needs the rate cap, which is where the saving is.
    const cameraIsThere = meetsEffectCapture(track.getSourceTrackSettings?.())
    if (cameraIsThere && restartsAtEffectCapture(track, target)) return

    // Marked before the camera is asked: the restart constraints are written
    // first, and a None picked while it answers must already find the mark.
    raisedForEffect.add(track)
    await changeLiveCapture(track, target, false)
  } catch (error) {
    console.warn('[LiveKit] SC-158: could not raise capture resolution for the effect:', error)
  }
}

/**
 * Put a live camera back on the capture the network profile chose, once the
 * effect is off. [SC-478]
 *
 * Without it the camera kept the effect's 720p until the page reloaded, paying
 * 4x the pixels in capture and encode for a picture that no longer needs them.
 * Safe where the raise is, and no more: it runs after `stopProcessor()`
 * resolved, so `mediaStreamTrack` is the camera again and no processor is
 * left to have its mask cleared by the frame-size change. Nothing on screen is
 * hidden by it, since the raw camera is what the user asked for.
 *
 * `applyConstraints` on the same track, not a restart: no camera re-open and no
 * second publish. What the camera restarts from is always written, even when the
 * camera is there already, or the next unmute would reopen at the effect
 * capture. A camera that refuses keeps those constraints matching what it still
 * runs. Best-effort and bounded, as the raise is.
 *
 * Clears the camera's {@link raisedForEffect} mark when it runs, and sets it again
 * if the camera cannot be asked, since the capture is then still the effect's and
 * the next "No effect" or publish may try again. Cleared up front, not at the
 * end, so a raise that comes in while the camera answers keeps its own mark.
 */
async function restoreCaptureAfterEffect(
  track: LocalVideoTrack,
  base: VideoConstraints
): Promise<void> {
  const wasRaised = raisedForEffect.delete(track)
  try {
    const settings = track.getSourceTrackSettings?.()
    const cameraIsThere =
      settings !== undefined &&
      settings.height === base.height &&
      settings.width === base.width &&
      // A rate that differs either way is asked again: a profile at 30 fps after
      // the effect's 24 is as much a reason as a camera that stayed at 30.
      (settings.frameRate === undefined ||
        Math.abs(settings.frameRate - base.frameRate) <= CAPTURE_FRAME_RATE_TOLERANCE_FPS)
    if (cameraIsThere) {
      setRestartConstraints(track, toMediaTrackConstraints(base))
      return
    }
    await changeLiveCapture(track, base, true)
  } catch (error) {
    if (wasRaised) raisedForEffect.add(track)
    console.warn('[LiveKit] SC-478: could not restore capture after the effect:', error)
  }
}

export function createVideoBackgroundLifecycle(
  deps: VideoBackgroundLifecycleDeps
): VideoBackgroundLifecycle {
  const {
    effectRef,
    processorRef,
    parkedRef,
    frameStatsRef,
    findLocalCameraTrack,
    makeFrameProcessedHandler,
    beginVideoToggleCooldown,
    announceEffect: announceToPickers,
    notify,
    publishState,
    unpublishCamera,
    requestCameraStart,
    noEffectCapture,
  } = deps

  /** Rooms that are gone. A late park must never land in one. */
  const retiredRooms = new WeakSet<Room>()
  /**
   * Bumped by every park and every clear. A park that awaited an image fetch
   * checks it afterwards, so a slow park for an older choice cannot overwrite a
   * newer one — including a newer "none".
   */
  let parkGeneration = 0

  // -------------------------------------------------------------------------
  // What is live, and what is wrong
  // -------------------------------------------------------------------------

  /**
   * The effect actually running: composited on the camera track, or parked and
   * ready while the camera is off. Distinct from `effectRef`, which is the
   * latest pick and may still be loading — a failed switch falls back to this.
   */
  let liveEffect: BackgroundEffect = NO_BACKGROUND_EFFECT
  let liveImageUrl: string | null = null
  let problem: BackgroundProblem | null = null
  /** The slow-device warning is given once per applied effect, not per frame. */
  let slowReported = false

  /** The last effect the pickers were told about, so a commit is not told twice. */
  let lastAnnouncedKey: string | null = null
  const announceEffect = (effect: BackgroundEffect, reverted: boolean): void => {
    lastAnnouncedKey = backgroundEffectKey(effect)
    announceToPickers(effect, reverted)
  }

  /**
   * Camera tracks held off the wire, with the guard that keeps them there and,
   * for a camera with no processor yet, its source track switched off.
   */
  const holds = new Map<
    LocalVideoTrack,
    {
      room: Room
      /** On `UpstreamResumed` and `Restarted`: keeps the current source off, and re-pauses. */
      guard: () => void
      onUnmuted: () => void
      /** The camera source the hold switched off, kept current across restarts. */
      disabledSource?: MediaStreamTrack
      /** See {@link readLocalCameraHoldPreview}: a None-to-effect switch on a live camera only. */
      preview?: { track: MediaStreamTrack; source: MediaStreamTrack; onMuted: () => void }
    }
  >()

  /**
   * Rooms whose camera start is waiting for its effect: from the gate being
   * asked to the camera being up and held (or unmuted), with a backstop for a
   * start that never reports back. Counted as held, so the camera reads as
   * "waiting for background" through the whole start rather than flicking to
   * "on" between the gate and the first composited frame.
   */
  const starting = new Map<Room, ReturnType<typeof setTimeout>>()

  const publish = () => publishState({ held: holds.size > 0 || starting.size > 0, problem })

  const markStarting = (room: Room): void => {
    if (retiredRooms.has(room)) return
    const existing = starting.get(room)
    if (existing) clearTimeout(existing)
    starting.set(
      room,
      setTimeout(() => {
        // Not while a processor for this Room is still loading. That start is
        // bounded and reports back on its own, with a notice if it failed;
        // letting go now would say "on" for a camera that is not, and say
        // nothing about why.
        if (processorStartPending(room)) markStarting(room)
        else doneStarting(room)
      }, PROCESSOR_START_TIMEOUT_MS)
    )
    if (!existing) publish()
  }

  function doneStarting(room: Room): void {
    const timer = starting.get(room)
    if (!timer) return
    clearTimeout(timer)
    starting.delete(room)
    publish()
  }

  const setProblem = (next: BackgroundProblem | null): void => {
    if (backgroundProblemsEqual(problem, next)) return
    problem = next
    publish()
    if (next) {
      notify({ type: 'warning', message: backgroundProblemMessage(next, 'game'), duration: 8000 })
    }
  }

  // -------------------------------------------------------------------------
  // Frames
  // -------------------------------------------------------------------------

  /** Processed frames seen per processor, for "has anything composited left yet". */
  const framesSeen = new WeakMap<BackgroundProcessorWrapper, number>()
  const frameWaiters = new WeakMap<
    BackgroundProcessorWrapper,
    Array<{ target: number; resolve: (produced: boolean) => void }>
  >()

  /**
   * Streak per window of frames. Every place that starts the samples over assigns
   * a new array to `frameStatsRef`, so the window's identity is also the moment a
   * streak must start over: a new processor, or an effect its samples do not
   * describe.
   */
  const slowStreaks = new WeakMap<BackgroundFrameStats[], SlowStreakTracker>()
  const slowStreakFor = (frames: BackgroundFrameStats[]): SlowStreakTracker => {
    let tracker = slowStreaks.get(frames)
    if (!tracker) {
      tracker = createSlowStreakTracker()
      slowStreaks.set(frames, tracker)
    }
    return tracker
  }

  const onFrame = (
    processor: BackgroundProcessorWrapper | null,
    stats: BackgroundFrameStats,
    room: Room
  ) => {
    if (!processor) return
    const seen = (framesSeen.get(processor) ?? 0) + 1
    framesSeen.set(processor, seen)
    const waiters = frameWaiters.get(processor)
    if (waiters?.length) {
      const due = waiters.filter((waiter) => waiter.target <= seen)
      frameWaiters.set(
        processor,
        waiters.filter((waiter) => waiter.target > seen)
      )
      for (const waiter of due) waiter.resolve(true)
    }

    // Only the processor on the call's camera counts towards "too slow".
    if (processor !== processorRef.current) return
    // One on a model the page has stepped down from is replaced, whoever stepped
    // down (the preview's slow check does it too), and says nothing about the
    // lighter model: its last frames, which keep coming until the camera is taken
    // down, would otherwise raise the notice the step-down exists to avoid.
    if (isProcessorOnSteppedDownModel(processor)) {
      rebuildOnSteppedDownModel(room, processor)
      return
    }
    const frames = frameStatsRef.current
    frames.push(stats)
    if (frames.length > 120) frames.splice(0, frames.length - 120)
    if (slowReported) return
    const health = evaluateBackgroundHealth(frames)
    const streak = slowStreakFor(frames)
    const sustained = streak.observe(health)
    // A busy streak may end in the lighter model: have it in the cache before the
    // rebuild asks, since a cold fetch there leaves the camera down for longer.
    if (streak.streaking) prewarmFallbackSegmentationModel()
    if (health !== 'disable' && !sustained) return
    // Before the standing-notice check below: the step-down is not a notice, and
    // a notice about something else (a failed switch, say) must not keep a slow
    // device on the heavier model.
    if (stepDownSegmentation()) {
      console.warn(
        '[LiveKit] SC-377: background effect is too slow; stepping down to the lighter model'
      )
      rebuildOnSteppedDownModel(room, processor)
      return
    }
    // A sustained `reduce-quality` on the lighter model already has nothing left
    // to give up, and is not bad enough to warn about: only `disable` does that.
    if (health !== 'disable') return
    if (problem) return
    // A warning, never a revert: turning the effect off would show the room
    // the user chose to hide. They decide whether to pick something lighter.
    slowReported = true
    console.warn('[LiveKit] SC-158: background effect is too slow on this device; warning the user')
    setProblem({ cause: 'slow', effect: effectRef.current })
  }

  /** Resolves true once `count` more frames have come out of `processor`. */
  const waitForFrames = (
    processor: BackgroundProcessorWrapper,
    count: number,
    timeoutMs: number
  ): Promise<boolean> =>
    new Promise((resolve) => {
      const target = (framesSeen.get(processor) ?? 0) + count
      let settled = false
      const settle = (produced: boolean) => {
        if (settled) return
        settled = true
        clearTimeout(timer)
        resolve(produced)
      }
      const timer = setTimeout(() => settle(false), timeoutMs)
      const waiters = frameWaiters.get(processor) ?? []
      waiters.push({ target, resolve: settle })
      frameWaiters.set(processor, waiters)
    })

  // -------------------------------------------------------------------------
  // Starting a processor
  // -------------------------------------------------------------------------

  /** First `init()`s under way, with the means to give up on each. */
  const pendingStarts = new Map<
    BackgroundProcessorWrapper,
    { room: Room; captureEffect: BackgroundEffect | null; giveUp: () => void }
  >()
  /** Processors nobody will use. One LiveKit has not started yet never starts now. */
  const discarded = new WeakSet<BackgroundProcessorWrapper>()
  /** Settles when a processor's first `init()` does, whatever its outcome. */
  const unsettledInits = new WeakMap<BackgroundProcessorWrapper, Promise<void>>()
  /** Rooms whose capture-time camera start was given up on, with the effect it was for. */
  const gaveUpStarts = new Map<Room, BackgroundEffect>()

  const processorStartPending = (room: Room): boolean =>
    [...pendingStarts.values()].some((start) => start.room === room)

  /**
   * Destroy a processor nobody will use.
   *
   * The library's `destroy()` does nothing while `init()` is running, and an
   * `init()` that lands afterwards starts the pipeline anyway: it segments its
   * camera for nobody, and its segmenter and WASM memory are never closed. So it
   * is destroyed again once a pending `init()` settles — and one that LiveKit has
   * not got round to starting is refused when it tries.
   */
  const discardProcessor = (processor: BackgroundProcessorWrapper): void => {
    if (discarded.has(processor)) return
    discarded.add(processor)
    destroyProcessorAndLateInit(processor, unsettledInits.get(processor))
  }

  /** Give up on a processor start that has not succeeded, and discard the processor. */
  const abandonProcessor = (processor: BackgroundProcessorWrapper): void => {
    const pending = pendingStarts.get(processor)
    if (pending) pending.giveUp()
    else discardProcessor(processor)
  }

  /** Give up on every camera start in `room` that is waiting for its processor. */
  const abandonCaptureStarts = (room: Room): void => {
    for (const start of [...pendingStarts.values()]) {
      if (start.room === room && start.captureEffect) start.giveUp()
    }
  }

  /**
   * Bound a processor's first `init()`, whoever calls it.
   *
   * Nothing beneath bounds it: it loads MediaPipe's script, WASM and model with
   * no timeout, and LiveKit awaits it inside `setProcessor` — on the mid-call
   * start, and inside `createLocalTracks` on every capture-time start, where the
   * connector's start-in-progress flag stays up behind it and turns away every
   * other camera start, a None's included. Bounded here, a hung load rejects:
   * `setProcessor` rejects with it, the camera start fails, and the connector
   * reports that ({@link VideoBackgroundLifecycle.handleCameraStartFailed}).
   *
   * Only the first: LiveKit's own restarts (unmute, device switch) re-run
   * `init()` on a pipeline that has already loaded, and are its business.
   *
   * `captureEffect` marks a capture-time processor. Its source is a camera that
   * LiveKit acquired for it and does not stop when the start fails, so a start
   * given up on — or one whose `init()` fails outright — stops it, and the
   * camera light goes off with it. The processor is discarded too, so LiveKit's
   * one retry, which brings a second camera and the same processor, is refused
   * and that camera stopped as well. A mid-call processor's source is the
   * published camera, which its caller takes down — stopping it here would have
   * LiveKit restart it, raw.
   */
  const boundFirstInit = (
    processor: BackgroundProcessorWrapper,
    room: Room,
    captureEffect: BackgroundEffect | null
  ): void => {
    if (typeof processor.init !== 'function') return
    const init = processor.init.bind(processor) as BackgroundProcessorWrapper['init']
    let started = false
    processor.init = (opts) => {
      if (started) return init(opts)
      // A capture start that will not come up: its camera goes off, and the
      // start is remembered as given up on for `captureEffect`, so the
      // connector's report of it either explains it (the effect would not
      // start) or, if the choice has moved on since, starts the camera again.
      const releaseCaptureStart = () => {
        if (!captureEffect) return
        gaveUpStarts.set(room, captureEffect)
        opts?.track?.stop?.()
      }
      if (discarded.has(processor)) {
        // Given up on already — or discarded for a newer choice in the window
        // where LiveKit has taken it from the capture defaults but is still
        // acquiring the camera. LiveKit retries a failed capture once, with a
        // looser device match and the same processor; that gets nothing either.
        releaseCaptureStart()
        return Promise.reject(new Error('SC-158: background processor start was given up on'))
      }
      const running = init(opts)
      const unsettled = running.then(
        () => undefined,
        () => undefined
      )
      unsettledInits.set(processor, unsettled)
      void unsettled.then(() => unsettledInits.delete(processor))

      return new Promise<void>((resolve, reject) => {
        let done = false
        const settle = () => {
          done = true
          clearTimeout(timer)
          pendingStarts.delete(processor)
        }
        const giveUp = (why: string) => {
          if (done) return
          settle()
          releaseCaptureStart()
          discardProcessor(processor)
          // Never "timeout" in the words, nor a retryable error name: the
          // connector starts a camera again on those, which would only wait
          // the bound out a second time.
          reject(new Error(`SC-158: background processor start ${why}`))
        }
        const timer = setTimeout(() => {
          console.warn('[LiveKit] SC-158: background processor did not start in time')
          giveUp('took too long')
        }, PROCESSOR_START_TIMEOUT_MS)
        pendingStarts.set(processor, {
          room,
          captureEffect,
          giveUp: () => giveUp('was given up for a newer choice'),
        })
        running.then(
          () => {
            if (done) return
            settle()
            started = true
            resolve()
          },
          (error: unknown) => {
            if (done) return
            settle()
            // Refused from here on, so LiveKit's retry cannot start a second
            // camera behind it and load the processor into that one.
            if (captureEffect) {
              releaseCaptureStart()
              discardProcessor(processor)
            }
            reject(error)
          }
        )
      })
    }
  }

  /**
   * Every processor is built here, so every one is monitored: the library reads
   * `onFrameProcessed` once, at construction, and a processor built without it
   * could never release a hold or report a slow device. And every one has its
   * start bounded ({@link boundFirstInit}).
   *
   * `captureEffect` is the effect a processor parked for a camera start is for;
   * null for one attached to a live camera.
   */
  const buildProcessor = (
    room: Room,
    options: ReturnType<typeof toProcessorOptions>,
    captureEffect: BackgroundEffect | null
  ): BackgroundProcessorWrapper | null => {
    const connectorFrameHandler = makeFrameProcessedHandler(room)
    let built: BackgroundProcessorWrapper | null = null
    built = createBackgroundProcessor({
      initial: options,
      onFrameProcessed: (stats: BackgroundFrameStats) => {
        onFrame(built, stats, room)
        connectorFrameHandler(stats)
      },
    })
    if (built) boundFirstInit(built, room, captureEffect)
    return built
  }

  // -------------------------------------------------------------------------
  // Holding a camera
  // -------------------------------------------------------------------------

  /**
   * Keep `track` off the wire and covered locally until it is released.
   *
   * `pauseUpstream()` alone does not hold: LiveKit resumes it by itself when
   * the source track unmutes and at the end of every `setMediaStreamTrack`
   * (restart, unmute, stopping a processor). {@link UPSTREAM_HELD_FLAG} makes
   * those resumes no-ops in the patched library; the guard re-pauses on
   * `UpstreamResumed` in case one still gets through (the patch not applied).
   * A track with no sender yet cannot be paused; `handleLocalSenderCreated`
   * pauses it the moment it gets one.
   */
  const registerHold = (room: Room, track: LocalVideoTrack, keepLocalPreview = false): void => {
    if (holds.has(track)) return
    setUpstreamHeld(track, true)
    // Synchronous on purpose: LiveKit emits `UpstreamResumed` *before* its
    // resume hands the track to the sender, so a source switched off here goes
    // out black; the re-pause itself waits for the resume (they share a lock).
    //
    // Review #4: a restart (a mobile tab coming back, LiveKit following the
    // default device, an unmute) replaces the camera source with a new one,
    // which LiveKit switches on. Only the old one was switched off, so without
    // `keepSourceOff` the rest of the hold had no black-frame cover — and
    // `setProcessor` later started on a live raw source. `Restarted` is defence
    // in depth: today every held restart resumes upstream first, but should a
    // LiveKit change stop `setMediaStreamTrack` emitting `UpstreamResumed`, the
    // new source would otherwise stay on for the rest of the hold.
    const guard = () => {
      if (!holds.has(track)) return
      keepSourceOff(track)
      void track.pauseUpstream?.()
    }
    // An unmute restarts the camera, then switches the new source on again
    // (`setTrackMuted(false)`), after the restart's own resume.
    const onUnmuted = () => {
      if (holds.has(track)) keepSourceOff(track)
    }
    track.on?.(TrackEvent.UpstreamResumed, guard)
    track.on?.(TrackEvent.Restarted, guard)
    track.on?.(TrackEvent.Unmuted, onUnmuted)
    // With no processor, what a resume puts back on the sender is the camera
    // itself. Switched off, the camera sends black instead, so even the moment
    // before the guard re-pauses shows nothing.
    let disabledSource: MediaStreamTrack | undefined
    let preview: { track: MediaStreamTrack; source: MediaStreamTrack; onMuted: () => void } | undefined
    if (!attachedProcessorOf(track)) {
      const source = track.mediaStreamTrack
      if (source && source.enabled !== false) {
        // Cloned before it is switched off, so the clone carries the picture.
        if (keepLocalPreview && !track.isMuted) preview = clonePreview(track, source)
        source.enabled = false
        disabledSource = source
      }
    }
    holds.set(track, { room, guard, onUnmuted, disabledSource, preview })
    // Offered before the hold is published, so the window never sees our camera held with nothing to show.
    if (preview) setHoldPreview(preview.track)
    publish()
  }

  /**
   * Keep a processor-less held camera's *current* source switched off.
   *
   * A restart gives the track a new source, switched on: it is switched off
   * too and becomes the one a release switches back on. A hold preview of the
   * old source is replaced by one of the new, cloned before it is switched off,
   * so the PiP window keeps showing the live camera and the old clone stops
   * capturing. A new source LiveKit left off (a muted camera's) is its to turn
   * on; the hold only takes over once it is on.
   *
   * With a processor attached, `mediaStreamTrack` is the processor's output,
   * and LiveKit restarts the processor on the new source itself.
   */
  const keepSourceOff = (track: LocalVideoTrack): void => {
    const held = holds.get(track)
    if (!held || attachedProcessorOf(track)) return
    const source = track.mediaStreamTrack
    if (!source) return
    if (source !== held.disabledSource) {
      held.disabledSource = undefined
      const old = held.preview
      if (old && old.source !== source) {
        // Offered in one step, never through null: the window swaps straight
        // to the new picture. The old clone is a capture of the old source and
        // would keep it running.
        const replacement = track.isMuted ? undefined : clonePreview(track, source)
        held.preview = replacement
        track.off?.(TrackEvent.Muted, old.onMuted)
        if (holdPreview === old.track) setHoldPreview(replacement?.track ?? null)
        old.track.stop()
      }
    }
    if (source.enabled !== false) {
      source.enabled = false
      held.disabledSource = source
    }
  }

  /**
   * The clone behind {@link readLocalCameraHoldPreview}. A camera turned off mid-hold keeps its hold (it
   * waits for its first frame again when turned back on), but not this: a clone is a capture of its own and
   * would keep the camera light on.
   */
  const clonePreview = (
    track: LocalVideoTrack,
    source: MediaStreamTrack
  ): { track: MediaStreamTrack; source: MediaStreamTrack; onMuted: () => void } | undefined => {
    if (typeof source.clone !== 'function' || source.readyState === 'ended') return undefined
    const clone = source.clone()
    clone.enabled = true
    const onMuted = () => endPreview(track, false)
    track.on?.(TrackEvent.Muted, onMuted)
    return { track: clone, source, onMuted }
  }

  /**
   * Withdraw and stop the hold preview. `handover`: the hold was released onto the effect, so the window
   * gets a moment to swap to it before the preview stops; every other way out stops it at once.
   */
  const endPreview = (track: LocalVideoTrack, handover: boolean): void => {
    const held = holds.get(track)
    const preview = held?.preview
    if (!preview) return
    held.preview = undefined
    track.off?.(TrackEvent.Muted, preview.onMuted)
    if (holdPreview === preview.track) setHoldPreview(null)
    if (handover) setTimeout(() => preview.track.stop(), HOLD_PREVIEW_HANDOVER_MS)
    else preview.track.stop()
  }

  const hold = (
    room: Room,
    track: LocalVideoTrack,
    { keepLocalPreview = false }: { keepLocalPreview?: boolean } = {}
  ): void => {
    if (holds.has(track)) return
    registerHold(room, track, keepLocalPreview)
    void track.pauseUpstream?.()
  }

  /**
   * Let the camera's frames through again: into a processor now, or the user chose none. The source
   * switched on is the current one — {@link keepSourceOff} keeps `disabledSource` up to date across
   * restarts, so a restart mid-hold cannot leave the live camera off after the release.
   */
  const enableSource = (track: LocalVideoTrack): void => {
    const held = holds.get(track)
    if (!held?.disabledSource) return
    held.disabledSource.enabled = true
    held.disabledSource = undefined
  }

  const removeHoldListeners = (track: LocalVideoTrack): void => {
    const held = holds.get(track)
    if (!held) return
    track.off?.(TrackEvent.UpstreamResumed, held.guard)
    track.off?.(TrackEvent.Restarted, held.guard)
    track.off?.(TrackEvent.Unmuted, held.onUnmuted)
  }

  /** Lift the local cover, then put the composited track back on the wire. */
  const release = async (track: LocalVideoTrack): Promise<void> => {
    if (!holds.has(track)) return
    endPreview(track, true)
    enableSource(track)
    removeHoldListeners(track)
    holds.delete(track)
    publish()
    // Cleared first: the patched library ignores every resume while it is set,
    // this one included. Any LiveKit resume still queued finds the track
    // already resumed, so this is the one that puts it back on the wire.
    setUpstreamHeld(track, false)
    await track.resumeUpstream?.()
  }

  /** Forget a hold without resuming: the track is going away. */
  const forgetHold = (track: LocalVideoTrack): void => {
    if (!holds.has(track)) return
    endPreview(track, false)
    enableSource(track)
    removeHoldListeners(track)
    holds.delete(track)
    // LiveKit's own resumes work again should the track be used after all.
    setUpstreamHeld(track, false)
    publish()
  }

  /**
   * Cameras {@link stopCamera} is taking down. Their unpublish is the lifecycle's own decision, made with
   * the park already settled (cleared for a failure, or about to be parked by the caller), so
   * {@link handleCameraUnpublished} leaves the park alone for them.
   */
  const stoppingCameras = new WeakSet<LocalVideoTrack>()

  /**
   * Take a camera down that cannot be shown: a held camera with no working
   * processor, or one started where effects cannot run. Unpublished rather than
   * muted, because unmuting a camera with no processor restarts it raw.
   */
  const stopCamera = async (room: Room, track: LocalVideoTrack): Promise<void> => {
    forgetHold(track)
    const attached = attachedProcessorOf(track)
    if (processorRef.current && processorRef.current === attached) processorRef.current = null
    frameStatsRef.current = []
    stoppingCameras.add(track)
    try {
      await unpublishCamera(room, track)
    } finally {
      stoppingCameras.delete(track)
    }
    // LiveKit stops the processor with the track; this also gives its WebGL
    // context back, which the library's own cleanup does not.
    if (attached) void destroyBackgroundProcessor(attached)
  }

  // -------------------------------------------------------------------------
  // Parking
  // -------------------------------------------------------------------------

  /** Drop the parked processor and take it out of its Room's capture defaults. */
  const clearParked = (): void => {
    const parked = parkedRef.current
    if (!parked) return
    parkedRef.current = null
    const defaults = captureDefaultsOf(parked.room)
    if (defaults.processor === parked.processor) defaults.processor = undefined
    // A processor LiveKit has started — including one still inside init() —
    // belongs to its track and is destroyed with it; the publish handler adopts
    // it. One it has not started would otherwise never release its canvases.
    if (!hasProcessorStarted(parked.processor)) discardProcessor(parked.processor)
  }

  /**
   * Review #12: no park while `room`'s camera is live with its processor. A camera toggle mutes and
   * unmutes that same track, processor and all; only a new camera track uses a park, and that becomes
   * possible when this one is unpublished ({@link handleCameraUnpublished} parks then) or taken down by
   * {@link stopCamera}, whose callers park. A park built now is a processor, and a WebGL context, nobody
   * uses. A start that finds nothing parked has the gate build one first, so nothing starts raw.
   *
   * The capture defaults are emptied too: a processor LiveKit already started there (the one this camera
   * adopted) must never be handed to another camera start.
   */
  const unparkWhileLive = (room: Room): void => {
    if (parkedRef.current?.room === room) clearParked()
    captureDefaultsOf(room).processor = undefined
  }

  const readyParkFor = (room: Room, effect: BackgroundEffect): ParkedCaptureProcessor | null => {
    const parked = parkedRef.current
    if (!parked || parked.room !== room) return null
    if (parked.effectKey !== backgroundEffectKey(effect)) return null
    if (hasProcessorStarted(parked.processor)) return null
    // Built before the page stepped down: starting a camera on it would load the
    // heavy model, and the publish would then replace it.
    if (isProcessorOnSteppedDownModel(parked.processor)) return null
    return captureDefaultsOf(room).processor === parked.processor ? parked : null
  }

  /**
   * Put a processor for `effect` into the room's capture defaults, so the next
   * camera start publishes an already-processed track.
   *
   * Clearing is as important as setting: a stale entry would start the camera
   * with an effect the user has since turned off. A failure leaves whatever is
   * parked alone — that is the effect still selected if this was a switch that
   * did not work out.
   *
   * `knownImageUrl` lets an apply that has just resolved the image skip a second
   * round-trip for the same one.
   */
  const parkWithImage = async (
    room: Room | null,
    effect: BackgroundEffect,
    knownImageUrl?: string | null
  ): Promise<ParkResult> => {
    if (!room) return { ok: true }
    const generation = ++parkGeneration
    const defaults = captureDefaultsOf(room)

    if (!shouldParkCaptureProcessor(effect.kind !== 'none', isBackgroundEffectAvailable())) {
      clearParked()
      defaults.processor = undefined
      return effect.kind === 'none' ? { ok: true } : { ok: false, cause: 'unsupported' }
    }
    if (retiredRooms.has(room)) return { ok: true }

    const wantedKey = backgroundEffectKey(effect)
    const parked = parkedRef.current
    if (parked && parked.room !== room) {
      // Its frame handler is bound to the old Room, and the old Room's defaults
      // are not consulted by this one. Never carried across.
      clearParked()
    } else if (
      parked &&
      !isProcessorOnSteppedDownModel(parked.processor) &&
      isParkedProcessorReusable(parked.effectKey, wantedKey, hasProcessorStarted(parked.processor))
    ) {
      // Same Room, same effect, never started. Make sure it is the one this
      // Room's capture will actually use.
      defaults.processor = parked.processor
      return { ok: true }
    }

    // Revalidated: this runs on connect and before every camera start, so it is
    // the path a rejoin takes, and the page-lifetime cache would keep a deleted
    // image alive.
    let imageUrl = knownImageUrl
    if (imageUrl === undefined) {
      const resolved = await resolveBackgroundImage(effect, { revalidate: true })
      if (generation !== parkGeneration || retiredRooms.has(room)) return { ok: true }
      if (resolved.gone) return { ok: false, cause: 'gone' }
      imageUrl = resolved.url
    }

    const options = toProcessorOptions(effect, imageUrl)
    // `disabled` is the library's pass-through — the raw camera. For an effect
    // that only means its image could not be had.
    if (options.mode === 'disabled') return { ok: false, cause: 'unavailable' }
    const processor = buildProcessor(room, options, effect)
    if (!processor) return { ok: false, cause: 'unsupported' }

    // Whatever was parked before is for an older choice.
    clearParked()
    parkedRef.current = { processor, effect, effectKey: wantedKey, room, imageUrl }
    defaults.processor = processor
    return { ok: true }
  }

  const parkCaptureProcessor = (room: Room | null, effect: BackgroundEffect) =>
    parkWithImage(room, effect)

  const parkBeforeConnect = async (
    room: Room,
    effect: BackgroundEffect,
    timeoutMs: number = CONNECT_PARK_TIMEOUT_MS
  ): Promise<void> => {
    // A warning from before — another Room, a pick made before the join — is
    // not true of a camera that needs no effect.
    if (effect.kind === 'none') setProblem(null)
    let timer: ReturnType<typeof setTimeout> | undefined
    const park = parkWithImage(room, effect).then(
      (result) => {
        if (!backgroundEffectsEqual(effectRef.current, effect)) return
        // Said now, not only when the camera is asked for: a deleted image or a
        // device that cannot run effects is worth knowing about on the way in,
        // camera on or off.
        if (!result.ok) setProblem({ cause: result.cause, effect })
        // And a warning about another effect — the one picked before this, in
        // the last call — is not true of this one, camera on or off.
        else if (problem && !backgroundEffectsEqual(problem.effect, effect)) setProblem(null)
      },
      (error) => {
        console.warn('[LiveKit] SC-158: could not pre-attach the background effect:', error)
      }
    )
    // The park carries on past the bound, and the camera-start gate waits for
    // it; the bound only stops the connect from waiting on a download.
    await Promise.race([
      park,
      new Promise<void>((resolve) => {
        timer = setTimeout(resolve, timeoutMs)
      }),
    ])
    if (timer) clearTimeout(timer)
  }

  // -------------------------------------------------------------------------
  // Turning it off
  // -------------------------------------------------------------------------

  /**
   * Tear the processor down and hand the caller back a raw camera. Only ever
   * for "none": the user asked for their camera as it is.
   *
   * True when a processor was taken off the camera track, which leaves a camera at
   * the effect's capture to put back. False for a camera that was raw already (a
   * "No effect" picked again, or from the problem banner), whose capture is
   * somebody else's now: the adaptive tier, or a reduction that has not ended. The
   * one raw camera that is still ours is the one this module raised
   * ({@link raisedForEffect}); the caller checks that itself.
   */
  const detachVideoBackground = async (room: Room | null): Promise<boolean> => {
    const processor = processorRef.current
    processorRef.current = null
    frameStatsRef.current = []

    const track = room ? findLocalCameraTrack(room) : null
    // Also a processor we never adopted — one LiveKit attached from the capture
    // defaults just before this ran. "None" has to mean none on the track, not
    // merely none in our bookkeeping.
    const attached = track ? attachedProcessorOf(track) : null
    const hadProcessor = Boolean(track && (processor || attached))
    if (track && hadProcessor) {
      try {
        await track.stopProcessor()
        reattachVideoElements(track)
      } catch {
        /* the track may already be gone */
      }
    }
    if (processor) await destroyBackgroundProcessor(processor)
    if (attached && attached !== processor) await destroyBackgroundProcessor(attached)
    if (track) await release(track)
    return hadProcessor
  }

  // -------------------------------------------------------------------------
  // Applying
  // -------------------------------------------------------------------------

  /**
   * Requests made so far, and a promise that settles on the next one. An apply
   * that is waiting on an image download races it against this, so a newer
   * request never queues behind a download that may take seconds.
   */
  let requestCount = 0
  let nextRequest = deferredSignal()

  function deferredSignal(): { promise: Promise<void>; fire: () => void } {
    let fire!: () => void
    const promise = new Promise<void>((resolve) => {
      fire = resolve
    })
    return { promise, fire }
  }

  /**
   * `work`'s result, or SUPERSEDED as soon as anything newer is requested.
   *
   * Any request at all, not only a different effect: the publish handler asks
   * again with the same effect when a camera track appears, and the waiting
   * apply would otherwise attach nothing to that track until the download
   * finished. The loop re-runs at once with the latest state, and a download
   * already in flight is shared rather than repeated (`backgroundLibrary`).
   */
  const unlessSuperseded = async <T>(
    work: Promise<T>,
    requestedBefore: number
  ): Promise<T | typeof SUPERSEDED> => {
    // Never rejects, so a download that fails after it was abandoned is not an
    // unhandled rejection.
    const settled = work.then(
      (value) => ({ value }),
      (error: unknown) => ({ error })
    )
    if (requestCount !== requestedBefore) return SUPERSEDED
    const superseded = nextRequest.promise.then((): typeof SUPERSEDED => SUPERSEDED)
    const winner = await Promise.race([settled, superseded])
    if (winner === SUPERSEDED) return SUPERSEDED
    if ('error' in winner) throw winner.error
    return winner.value
  }

  /**
   * `track.setProcessor(processor)`, bounded, and given up on as soon as a
   * different effect is picked or the Room goes.
   *
   * The processor's `init()` is bounded where it is built; this bounds the rest
   * of `setProcessor` too — the wait for the track's change lock before `init()`
   * and the sender swap after it. A hung one would hold the camera for good,
   * keep `whenSettled` — and with it every camera start and device switch —
   * waiting, and leave a None pick queued behind it.
   */
  const startProcessor = (
    room: Room,
    track: LocalVideoTrack,
    processor: BackgroundProcessorWrapper,
    effect: BackgroundEffect
  ): Promise<'started' | 'failed' | 'timed-out' | 'abandoned'> =>
    new Promise((resolve) => {
      let done = false
      const finish = (outcome: 'started' | 'failed' | 'timed-out' | 'abandoned') => {
        if (done) return
        done = true
        clearTimeout(timer)
        resolve(outcome)
      }
      const timer = setTimeout(() => {
        console.warn('[LiveKit] SC-158: background processor did not start in time')
        finish('timed-out')
      }, PROCESSOR_START_TIMEOUT_MS)
      track.setProcessor(processor).then(
        () => finish('started'),
        (error: unknown) => {
          console.warn('[LiveKit] SC-158: background processor failed to start:', error)
          finish('failed')
        }
      )
      // Any request wakes this; only a different effect or a retired Room
      // gives up. The same effect asked again (a publish) keeps waiting.
      const watch = () => {
        void nextRequest.promise.then(() => {
          if (done) return
          if (!backgroundEffectsEqual(effectRef.current, effect) || retiredRooms.has(room)) {
            finish('abandoned')
          } else {
            watch()
          }
        })
      }
      watch()
    })

  /**
   * The effect `room`'s camera is showing, or will start with: the one
   * composited on it, or the one parked in its capture defaults.
   */
  const runningEffect = (
    room: Room
  ): { effect: BackgroundEffect; imageUrl: string | null } | null => {
    if (liveEffect.kind !== 'none') return { effect: liveEffect, imageUrl: liveImageUrl }
    const parked = parkedRef.current
    if (!parked || parked.room !== room) return null
    if (captureDefaultsOf(room).processor !== parked.processor) return null
    return { effect: parked.effect, imageUrl: parked.imageUrl ?? null }
  }

  /** Make `effect` the selection: every picker told, and saved if asked. */
  const commit = (effect: BackgroundEffect, persist: boolean): void => {
    if (lastAnnouncedKey !== backgroundEffectKey(effect)) announceEffect(effect, false)
    if (!persist) return
    void saveBackgroundEffectPreference(effect)
    // Saved: a re-run for the same effect (a publish merged in) has nothing
    // left to save. Without this, the kept flag (review #1) saved it twice.
    if (backgroundEffectsEqual(effectRef.current, effect)) latestPersist = false
  }

  /**
   * `keepNotice`: a notice that does not hold the camera (a slow device, a failed
   * switch) stays, because this success is not about it. The step-down's rebuild
   * asks for it; erasing a "your pick failed" notice there would be silent.
   */
  const succeed = (
    effect: BackgroundEffect,
    imageUrl: string | null,
    { keepNotice = false }: { keepNotice?: boolean } = {}
  ): void => {
    liveEffect = effect
    liveImageUrl = imageUrl
    slowReported = false
    if (keepNotice && problem && !backgroundProblemHoldsCamera(problem)) return
    setProblem(null)
  }

  /**
   * `effect` cannot be shown. Never raw, and never a cleared choice.
   *
   * With an older effect still running (`fallback`), that one stays on and is
   * the selection again. Otherwise the selection stays `effect` and the camera
   * stays held: taken down if it is up, kept from starting if it is not.
   *
   * Only while `effect` is still the latest pick. One made meanwhile — even
   * during the switch back to the fallback — is the user's newer word: the
   * latest-wins loop applies it next, and resetting the selection here would
   * lose it on the track, in the pickers and in the saved preference.
   */
  const fail = async (
    room: Room,
    effect: BackgroundEffect,
    cause: BackgroundProblemCause,
    fallback: BackgroundEffect | null
  ): Promise<void> => {
    console.warn(`[LiveKit] SC-158: background effect could not be applied (${cause})`)
    if (!backgroundEffectsEqual(effectRef.current, effect)) return
    // A camera start waiting for this effect lets go last, so the camera
    // reads as waiting until the problem that keeps it off is there to say so.
    try {
      if (fallback && fallback.kind !== 'none') {
        effectRef.current = fallback
        // The pick that was to be saved failed; the fallback is not a new pick.
        latestPersist = false
        announceEffect(fallback, true)
        setProblem({ cause: 'switch-failed', effect, stillUsing: fallback })
        const track = findLocalCameraTrack(room)
        if (!track) return
        // Released only onto a processor — the fallback's. A camera without
        // one would resume as the room behind the user; it restarts through
        // the gate with the fallback instead.
        if (attachedProcessorOf(track)) await release(track)
        else await stopCamera(room, track)
        return
      }
      const track = findLocalCameraTrack(room)
      if (track) await stopCamera(room, track)
      if (parkedRef.current?.room === room) clearParked()
      liveEffect = NO_BACKGROUND_EFFECT
      liveImageUrl = null
      setProblem({ cause, effect })
    } finally {
      doneStarting(room)
    }
  }

  /** The image an effect needs, or why it cannot be had. */
  const imageFailure = (
    effect: BackgroundEffect,
    resolved: { url: string | null; gone: boolean }
  ): BackgroundProblemCause | null => {
    if (effect.kind === 'none' || effect.kind === 'blur') return null
    if (resolved.gone) return 'gone'
    return resolved.url ? null : 'unavailable'
  }

  /** Processors whose replacement has been asked for. */
  const steppedDownRebuilds = new WeakSet<BackgroundProcessorWrapper>()

  /**
   * The page has stepped down to the lighter segmentation model: replace the
   * camera's processor, which was built with the heavier one.
   *
   * Asked for as an apply of the effect already selected, so it is serialised
   * with the user's picks and a newer pick still wins; the apply finds the
   * processor on the stepped-down model and rebuilds it. A processor parked for a
   * camera start is dropped first, or the park would hand the heavy model back.
   */
  const rebuildOnSteppedDownModel = (room: Room, processor: BackgroundProcessorWrapper): void => {
    // Once per processor: its frames keep coming while the camera comes down.
    if (steppedDownRebuilds.has(processor)) return
    steppedDownRebuilds.add(processor)
    frameStatsRef.current = []
    if (parkedRef.current?.room === room) clearParked()
    void applyVideoBackground(room, effectRef.current, { persist: false })
  }

  /**
   * Put a camera that is raw again back on the capture the network profile
   * chose, once the effect is off. Never throws: the callers are not in a
   * position to do anything about a camera that will not change.
   *
   * Only for a camera with no processor (its `mediaStreamTrack` is then the
   * camera itself), and only while "none" is still the pick. A newer effect
   * raises the capture again, and this one issuing its request after that raise
   * would leave the camera below 720p under it (SC-158). The check and the
   * request are in one synchronous step, so no pick can fall between them.
   */
  const restoreProfileCapture = async (track: LocalVideoTrack): Promise<void> => {
    try {
      const base = noEffectCapture?.()
      if (!base || attachedProcessorOf(track) || effectRef.current.kind !== 'none') return
      await restoreCaptureAfterEffect(track, base)
    } catch (error) {
      // Not the restore's own failure (that one is reported inside it): reading
      // the profile capture threw, which says something else about the setup.
      console.warn('[LiveKit] SC-478: could not read the profile capture:', error)
    }
  }

  /** One apply, for one effect. Never throws. */
  const applyOnce = async (
    room: Room | null,
    effect: BackgroundEffect,
    requestedBefore: number,
    persist: boolean
  ): Promise<void> => {
    // No Room yet — the connector is mounted before the first join, and the
    // pre-join dialog announces its choice to it — or a Room is being replaced.
    // There is nothing to apply to: the intent is recorded, and the connect
    // path parks it. A pick made here is saved as it stands, since there is no
    // running effect to check it against.
    if (!room) {
      // A warning is about the effect it names. Turning that effect off, or
      // picking another, answers it, Room or not — otherwise a warning from the
      // last call stays up over a choice it says nothing about.
      if (problem && !backgroundEffectsEqual(problem.effect, effect)) setProblem(null)
      if (persist) void saveBackgroundEffectPreference(effect)
      return
    }

    // Superseded results are not announced or reported. The latest-wins loop
    // applies the newer choice next, and announcing this one first would flick
    // every picker to an effect the user has already moved past.
    const isLatest = () => backgroundEffectsEqual(effectRef.current, effect)

    try {
      // 'none' is always honoured, even where the feature is switched off —
      // that is the path that turns an effect back off, and the only one that
      // may show the raw camera.
      if (effect.kind === 'none') {
        // A camera starting with the effect is given up on, not waited for: its
        // processor may be stuck loading, and the camera None asks for would
        // queue behind it. The failed start comes back to
        // `handleCameraStartFailed`, which starts the camera afresh, raw.
        abandonCaptureStarts(room)
        const strippedProcessor = await detachVideoBackground(room)
        // Out of the park and out of the Room's capture defaults, or the next
        // camera start comes up with the effect the user just turned off.
        await parkWithImage(room, NO_BACKGROUND_EFFECT)
        liveEffect = NO_BACKGROUND_EFFECT
        liveImageUrl = null
        setProblem(null)
        // Nothing waits for a background any more.
        doneStarting(room)
        if (isLatest()) {
          commit(NO_BACKGROUND_EFFECT, persist)
          requestCameraStart(room)
          // After the commit, so the selection never waits on the camera, and
          // inside this apply, so a newer effect pick raises the capture only
          // once this has settled or its bound has passed. Only for a pick still
          // standing: a newer effect raises it again, and two resolution changes
          // in a row would only cost the camera a blink. And only for a camera at
          // the effect's capture because of the effect: one a processor was taken
          // off, or one this module raised that never got its processor (a None
          // picked while the raise was still answering). A camera that was raw
          // already is somebody else's, and writing the profile over it would undo
          // whatever the adaptive tier or a screen-share reduction set.
          const cameraAfterDetach = findLocalCameraTrack(room)
          if (cameraAfterDetach && (strippedProcessor || raisedForEffect.has(cameraAfterDetach))) {
            await restoreProfileCapture(cameraAfterDetach)
          }
        }
        return
      }

      if (!isBackgroundEffectAvailable()) {
        // Nothing here can run it. The camera is off unless the user chose to
        // use it without a background on this device.
        if (isLatest()) commit(effect, persist)
        if (readCameraWithoutBackgroundOnThisDevice()) {
          setProblem(null)
          return
        }
        const track = findLocalCameraTrack(room)
        if (track) await stopCamera(room, track)
        setProblem({ cause: 'unsupported', effect })
        return
      }

      // The first pick starts the download of the WASM and the model now, not
      // when init() asks for them: init() is timed, and a cold download of the
      // default model is most of what it waits for. A no-op after the first call.
      prewarmBackgroundAssets(effect)

      // Effect to effect: the old one stays on screen, and stays the
      // selection, until the new one is ready. The old one is whatever is
      // running — or parked in this Room's capture defaults, which is what a
      // camera starting now comes up with: a pick made while the camera is
      // starting must not cost the user the effect it is starting with.
      const running = runningEffect(room)
      const fallback =
        running && !backgroundEffectsEqual(running.effect, effect) ? running.effect : null
      const fallbackImageUrl = running?.imageUrl ?? null

      // A raw camera, if one is on, comes off the wire and off the screen
      // before anything is awaited — the selection never lags it.
      const cameraAtPick = findLocalCameraTrack(room)
      // The one hold that keeps a local picture: a live raw camera was the current view (see
      // `readLocalCameraHoldPreview`).
      if (cameraAtPick && !attachedProcessorOf(cameraAtPick)) {
        hold(room, cameraAtPick, { keepLocalPreview: true })
      }
      if (!fallback && isLatest()) commit(effect, persist)

      // The step-down's rebuild of the effect that is running, on a camera whose
      // processor is on the model the page has stepped down from. Nothing about
      // the effect has changed, so nothing about it is asked again: re-checking
      // an image over the network could take a working camera down for a blip,
      // which a slow verdict never did before. The image that is live is the one
      // the new processor starts with.
      const steppingDown =
        running !== null &&
        !fallback &&
        cameraAtPick !== null &&
        isProcessorOnSteppedDownModel(attachedProcessorOf(cameraAtPick)) &&
        (!requiresBackgroundImage(effect) || Boolean(running.imageUrl))

      // `revalidate`: this is an apply, so the page-lifetime object-URL cache
      // must not answer. Without it an admin's deletion is invisible until a
      // full reload.
      const resolved = steppingDown
        ? { url: running.imageUrl, gone: false }
        : await unlessSuperseded(
            resolveBackgroundImage(effect, { revalidate: true }),
            requestedBefore
          )
      if (resolved === SUPERSEDED || !isLatest()) return
      const missing = imageFailure(effect, resolved)
      if (missing) {
        await fail(room, effect, missing, fallback)
        return
      }
      const imageUrl = resolved.url

      // Decoded while the old effect is still live, so the swap below is a
      // GPU upload rather than a download with no background behind the user.
      if (imageUrl && !steppingDown) {
        try {
          const loaded = await unlessSuperseded(preloadBackgroundImage(imageUrl), requestedBefore)
          if (loaded === SUPERSEDED || !isLatest()) return
        } catch {
          await fail(room, effect, 'unavailable', fallback)
          return
        }
      }
      const options = toProcessorOptions(effect, imageUrl)
      if (options.mode === 'disabled') {
        await fail(room, effect, 'unavailable', fallback)
        return
      }

      // Looked up again: the camera may have started or stopped meanwhile.
      const track = findLocalCameraTrack(room)
      if (!track) {
        // Camera off: ready for the next camera start.
        const parked = await parkWithImage(room, effect, imageUrl)
        if (!isLatest()) return
        if (!parked.ok) {
          await fail(room, effect, parked.cause, fallback)
          return
        }
        succeed(effect, imageUrl)
        if (fallback) commit(effect, persist)
        requestCameraStart(room)
        return
      }

      const attached = attachedProcessorOf(track)
      if (attached && isProcessorOnSteppedDownModel(attached)) {
        // The page has stepped down to the lighter model, and a processor's model
        // is fixed when it is built: this one has to be replaced, not switched.
        // The camera comes back up through the gate with a new one, as after any
        // failure that takes a processor down, so the raw camera is never on the
        // wire in between. It reads as waiting, not off, throughout (see the
        // abandoned start below) — unless the user has turned the camera off:
        // nothing will start it, so nothing may wait for it (the muted case
        // below does the same, and the park is ready for the next start).
        const wasOn = !track.isMuted
        if (wasOn && wantsProcessing(effect)) markStarting(room)
        await stopCamera(room, track)
        const rebuilt = await parkWithImage(room, effect, imageUrl)
        if (!isLatest()) return
        if (!rebuilt.ok) {
          await fail(room, effect, rebuilt.cause, null)
          return
        }
        succeed(effect, imageUrl, { keepNotice: true })
        if (fallback) commit(effect, persist)
        if (wasOn) requestCameraStart(room)
        return
      }
      if (attached) {
        // Effect to effect, on the processor already running. Never
        // `setProcessor` on a track that has one: LiveKit stops the old one
        // first, which puts the raw camera on the sender until the new one is
        // ready.
        //
        // Not held: the library is patched (patches/@livekit+track-processors)
        // so that a swap is atomic — the new image is prepared while the old
        // background renders, swapped in at once, and `switchTo` resolves once
        // it is in place.
        // Review #6: a switch can outlive its camera. The first-frame timeout
        // (`releaseOnFirstFrame`) fails and takes the camera down while this
        // awaits; carrying on would clear that problem in `succeed` and adopt a
        // destroyed processor.
        const switchedCameraGone = () =>
          findLocalCameraTrack(room) !== track || attachedProcessorOf(track) !== attached
        try {
          await attached.switchTo(options)
        } catch (error) {
          console.warn('[LiveKit] SC-158: background switch failed:', error)
          if (fallback) {
            try {
              await attached.switchTo(toProcessorOptions(fallback, fallbackImageUrl))
              if (switchedCameraGone()) {
                // The pick still failed: reset the selection to the fallback
                // and say so. Only the gone track is not ours to adopt. A
                // first-frame failure already reported for this pick (review
                // #6) stands as it is.
                if (isLatest() && !(problem && backgroundEffectsEqual(problem.effect, effect))) {
                  await fail(room, effect, 'processor-failed', fallback)
                }
                return
              }
              await fail(room, effect, 'processor-failed', fallback)
              return
            } catch {
              /* nothing left running: hold below */
            }
          }
          await fail(room, effect, 'processor-failed', null)
          return
        }
        if (switchedCameraGone()) {
          // The switch itself worked, and the next camera (an RC3 republish)
          // comes up with this pick, so it is still the selection and is saved.
          // Not when the first-frame timeout failed this pick meanwhile
          // (review #6); an older warning about another effect does not count.
          if (fallback && isLatest() && !(problem && backgroundEffectsEqual(problem.effect, effect))) {
            commit(effect, persist)
          }
          return
        }
        // Review #10: the slow-device check judges the effect now running, not
        // the samples of the one it replaced — `succeed` re-arms the warning,
        // and those would raise it again at once, for an effect never measured.
        frameStatsRef.current = []
        // A camera still held here is waiting for its first composited frame,
        // and every such hold has a waiter (`releaseOnFirstFrame`) that
        // releases it on that frame; the switch does not change the processor.
        processorRef.current = attached
      } else if (track.isMuted) {
        // A camera the user turned off, with nothing to process it. Attaching
        // to a stopped camera fights the library, and unmuting it later would
        // restart it raw — so it comes down, and turning it back on goes
        // through the gate with the effect parked.
        await stopCamera(room, track)
        const parked = await parkWithImage(room, effect, imageUrl)
        if (!isLatest()) return
        if (!parked.ok) {
          await fail(room, effect, parked.cause, null)
          return
        }
      } else {
        // The first effect on a camera that is on. Held since the pick.
        hold(room, track)
        await raiseCaptureResolutionForEffect(track)
        if (!isLatest()) return
        // Built in the TARGET mode rather than 'disabled' + switchTo: the
        // library only loads the background image inside init() when imagePath
        // was set at construction.
        const processor = buildProcessor(room, options, null)
        if (!processor) {
          await fail(room, effect, 'unsupported', null)
          return
        }
        processorRef.current = processor
        frameStatsRef.current = []
        // No processor on the track, so LiveKit's stop-the-old-one step — the
        // one that would resume the sender with the raw camera — is not
        // entered, and the hold survives.
        const started = await startProcessor(room, track, processor, effect)
        if (started !== 'started') {
          processorRef.current = null
          abandonProcessor(processor)
          if (started === 'abandoned') {
            // A different pick, or the Room went. LiveKit may still be inside
            // `setProcessor`, holding the lock every other change to this track
            // waits on — `stopProcessor` included — so the camera is taken
            // down instead; unpublishing does not need the lock. The newer
            // pick starts it afresh through the gate.
            //
            // Still waiting, as far as anyone can see, until that pick's own
            // camera start takes over or it fails: "off" in between would flick
            // the camera to red for everyone while its image downloads.
            if (wantsProcessing(effectRef.current)) markStarting(room)
            await stopCamera(room, track)
            return
          }
          await fail(room, effect, 'processor-failed', null)
          return
        }
        // The processor reads the camera now; its output is all a resume can
        // put on the wire.
        enableSource(track)
        // Cut short by any newer request, so a None or another pick never
        // waits out the warm-up. The processor is attached, so the next apply
        // can switch or detach it; until then the hold waits for its first
        // frame on its own, as every hold on an attached processor does.
        const produced = await unlessSuperseded(
          waitForFrames(processor, 1, FIRST_FRAME_TIMEOUT_MS),
          requestedBefore
        )
        if (produced === SUPERSEDED) {
          void releaseOnFirstFrame(room, track, processor)
          return
        }
        if (!isLatest()) return
        if (produced) {
          await release(track)
        } else if (track.isMuted) {
          // Turned off during the warm-up — by the user, or by the connector's
          // poor-connection auto-disable. A stopped camera produces no frames,
          // which says nothing about the effect: the hold stays, and turning it
          // back on waits for the first frame again, as any held start does.
          void releaseOnFirstFrame(room, track, processor)
        } else {
          await fail(room, effect, 'processor-failed', null)
          return
        }
      }

      succeed(effect, imageUrl)
      if (fallback && isLatest()) commit(effect, persist)
      const live = findLocalCameraTrack(room)
      if (live && attachedProcessorOf(live)) {
        unparkWhileLive(room)
      } else {
        // So the next camera start — after the camera was taken down above —
        // comes up with this effect too.
        await parkWithImage(room, effect, imageUrl)
      }
    } catch (error) {
      console.warn('[LiveKit] SC-158: failed to apply background effect:', error)
      if (isLatest()) await fail(room, effect, 'processor-failed', null)
    }
  }

  let applying: Promise<void> | null = null
  let rerunRequested = false
  let latestRoom: Room | null = null
  let latestPersist = true
  let pickCount = 0

  /**
   * Apply (or clear) the background effect on the camera.
   *
   * Records the intent first, so it survives the camera being off and is
   * picked up by the next camera start.
   *
   * Latest wins. Applies are async and can be requested from several places at
   * once (the picker, the durable-preference load, a publish). One runs at a
   * time; a request made meanwhile records the intent and has the running one
   * go round again with the latest. Every caller's promise settles on that
   * final state.
   *
   * The running apply is abandoned, not waited for, while it waits on an image
   * download: a Blur or None picked during a stalled download is applied at
   * once, and the stale download's result is dropped.
   */
  const applyVideoBackground = (
    room: Room | null,
    effect: BackgroundEffect,
    { persist = true }: ApplyOptions = {}
  ): Promise<void> => {
    const previous = effectRef.current
    effectRef.current = effect
    if (!backgroundEffectsEqual(previous, effect)) pickCount += 1
    latestRoom = room
    // Review #1: requests merge into the running apply, and the publish and
    // unmute handlers re-ask for the same effect with `persist: false`. That
    // must not cancel the save of the user's own pick still in flight — a
    // camera publishing during the image download lost it. A different effect
    // replaces the pick, and its own flag with it.
    latestPersist =
      persist || (applying !== null && latestPersist && backgroundEffectsEqual(previous, effect))
    requestCount += 1
    nextRequest.fire()
    nextRequest = deferredSignal()
    if (applying) {
      rerunRequested = true
      return applying
    }

    applying = (async () => {
      try {
        do {
          rerunRequested = false
          await applyOnce(latestRoom, effectRef.current, requestCount, latestPersist)
        } while (rerunRequested)
      } finally {
        applying = null
      }
    })()
    return applying
  }

  const whenSettled = async (): Promise<void> => {
    while (applying) await applying
  }

  // -------------------------------------------------------------------------
  // The camera-start gate
  // -------------------------------------------------------------------------

  /** A camera that is on, with its processor attached: nothing about it is starting. */
  const cameraUpWithProcessor = (room: Room): boolean => {
    const track = findLocalCameraTrack(room)
    return Boolean(track && !track.isMuted && attachedProcessorOf(track))
  }

  const prepareCameraStart = async (room: Room): Promise<boolean> => {
    if (retiredRooms.has(room)) return false
    // Only a camera that will wait for its effect reads as waiting. Never for
    // None, nor for an effect this device runs without a background: no hold,
    // no "waiting", nothing sent to anyone. Nor for a camera already up and
    // composited — the restore after every reconnect asks again for it, LiveKit
    // reports nothing for turning on a camera that is on, and the mark would
    // stand for its whole backstop over a camera everyone can see.
    const marked = wantsProcessing(effectRef.current) && !cameraUpWithProcessor(room)
    if (marked) markStarting(room)
    let allowed = false
    try {
      allowed = await decideCameraStart(room)
      return allowed
    } finally {
      // An allowed start with an effect is still waiting for it until the
      // camera is up and held for its first composited frame.
      const waiting = allowed && wantsProcessing(effectRef.current)
      if (marked && !waiting) doneStarting(room)
      // Review #7: a None picked while the gate waited let the mark go, and
      // the effect picked again after it is what this start comes up with.
      else if (waiting && !starting.has(room) && !cameraUpWithProcessor(room)) markStarting(room)
    }
  }

  const decideCameraStart = async (room: Room): Promise<boolean> => {
    // An apply under way may be about to hold, park or fail; decide after it.
    await whenSettled()
    const effect = effectRef.current
    if (effect.kind === 'none') {
      // Nothing about a background can keep this camera off any more.
      setProblem(null)
      return true
    }

    if (!isBackgroundEffectAvailable()) {
      if (readCameraWithoutBackgroundOnThisDevice()) {
        if (problem?.cause === 'unsupported') setProblem(null)
        return true
      }
      setProblem({ cause: 'unsupported', effect })
      return false
    }

    const existing = findLocalCameraTrack(room)
    if (existing) {
      // Turning a muted camera back on restarts it, processor and all, so one
      // that has a processor comes back composited. One that is on already
      // has nothing to wait for (a hold, if it has one, says so itself).
      if (attachedProcessorOf(existing)) {
        if (!existing.isMuted) doneStarting(room)
        return true
      }
      // One without would come back raw. Take it down and start afresh below.
      await stopCamera(room, existing)
    }

    if (readyParkFor(room, effect)) return true
    // Tried again on every camera start, so a network blip or a processor that
    // failed once does not keep the camera off for the rest of the call.
    const parked = await parkWithImage(room, effect)
    if (!backgroundEffectsEqual(effectRef.current, effect)) return decideCameraStart(room)
    if (!parked.ok) {
      liveEffect = NO_BACKGROUND_EFFECT
      setProblem({ cause: parked.cause, effect })
      return false
    }
    if (!readyParkFor(room, effect)) {
      // Review #7: this park was overtaken by a newer one (A, None, A picked
      // while it fetched) and parked nothing. The apply that overtook it parks
      // this same effect: decide once it has, rather than refuse with no
      // reason given. A retired Room has no apply to wait for.
      return applying !== null && !retiredRooms.has(room) ? decideCameraStart(room) : false
    }
    if (backgroundProblemHoldsCamera(problem)) setProblem(null)
    return true
  }

  const handleCameraStartFailed = (room: Room): boolean => {
    const effect = effectRef.current
    const gaveUpOn = gaveUpStarts.get(room) ?? null
    gaveUpStarts.delete(room)
    if (retiredRooms.has(room)) return false

    const parked = parkedRef.current
    // A device error fails before LiveKit reaches the processor, which is
    // still unstarted then. One that LiveKit started — or whose start was
    // given up on — with no camera to show for it, is the effect that would
    // not start.
    const spentPark =
      parked && parked.room === room && hasProcessorStarted(parked.processor) ? parked : null
    // R9-N2: the return to the same effect (A, None, A) whose apply has not
    // parked yet — an image still being revalidated. That apply parks and asks
    // for the camera itself; reported here, the user would get a false
    // "couldn't start" and an off-blink. Never over a spent processor: that
    // start failed for real.
    const applyWillPark = applying !== null && !spentPark

    if (
      gaveUpOn &&
      (!backgroundEffectsEqual(gaveUpOn, effect) || readyParkFor(room, effect) || applyWillPark)
    ) {
      // Given up on for a newer choice — None, or a pick made while it hung.
      // That includes a return to the same effect (A, None, A): its fresh
      // processor is parked, unstarted, which a start that failed for real
      // never leaves behind — or about to be, by the apply under way. Nothing
      // the user still wants failed: start the camera with what they want now,
      // through the gate. Waiting until then, unless that is None.
      if (wantsProcessing(effect)) markStarting(room)
      else doneStarting(room)
      // Asked for once: an apply that is yet to park asks when it has.
      const parkPending = backgroundEffectsEqual(gaveUpOn, effect) && !readyParkFor(room, effect)
      if (!parkPending) requestCameraStart(room)
      return true
    }

    if (
      effect.kind === 'none' ||
      !isBackgroundEffectAvailable() ||
      (!spentPark && !gaveUpOn) ||
      findLocalCameraTrack(room)
    ) {
      doneStarting(room)
      return false
    }
    if (spentPark) {
      parkedRef.current = null
      const defaults = captureDefaultsOf(room)
      if (defaults.processor === spentPark.processor) defaults.processor = undefined
      discardProcessor(spentPark.processor)
    }
    liveEffect = NO_BACKGROUND_EFFECT
    liveImageUrl = null
    // Before the start lets go, so the camera reads as waiting throughout.
    setProblem({ cause: 'processor-failed', effect })
    doneStarting(room)
    return true
  }

  const forgetCameraStart = (room: Room): void => {
    // Left behind, it turns the next real failure into a silent restart.
    gaveUpStarts.delete(room)
    if (retiredRooms.has(room)) return
    // Another start for this Room is still loading its processor, or an apply
    // is about to park and ask for the camera: the waiting state and the park
    // are theirs.
    if (processorStartPending(room) || applying !== null) return
    const parked = parkedRef.current
    // Only a park this start used up; a fresh one is the next start's. A camera
    // that is up may be running it.
    if (
      parked &&
      parked.room === room &&
      hasProcessorStarted(parked.processor) &&
      !findLocalCameraTrack(room)
    ) {
      parkedRef.current = null
      const defaults = captureDefaultsOf(room)
      if (defaults.processor === parked.processor) defaults.processor = undefined
      discardProcessor(parked.processor)
    }
    doneStarting(room)
  }

  // -------------------------------------------------------------------------
  // LiveKit events
  // -------------------------------------------------------------------------

  /** Whether `effect` needs the camera processed (or kept off) here. */
  const wantsProcessing = (effect: BackgroundEffect): boolean =>
    effect.kind !== 'none' &&
    (isBackgroundEffectAvailable() || !readCameraWithoutBackgroundOnThisDevice())

  /**
   * A camera track has just been given its sender, before it is negotiated.
   *
   * Nothing goes on the wire until something composited has come out of the
   * track: a capture-time processor's first frame is the earliest moment the
   * effect is provably on. A track that is already producing — a reconnect
   * republishing a running camera — is left alone.
   */
  /**
   * Release a held camera on the first composited frame out of `processor`, or
   * fail if none comes.
   *
   * A camera turned off before that frame keeps its hold: nothing composited
   * has gone out yet, so turning it back on waits for the first frame again
   * ({@link handleCameraUnmuted}) rather than resume a pipeline nobody has seen
   * work.
   */
  const releaseOnFirstFrame = async (
    room: Room,
    track: LocalVideoTrack,
    processor: BackgroundProcessorWrapper
  ): Promise<void> => {
    const produced = await waitForFrames(processor, 1, FIRST_FRAME_TIMEOUT_MS)
    if (attachedProcessorOf(track) !== processor || !holds.has(track)) return
    if (produced) {
      await release(track)
    } else if (!track.isMuted) {
      await fail(room, effectRef.current, 'processor-failed', null)
    }
  }

  const handleLocalSenderCreated = async (
    room: Room,
    sender: RTCRtpSender,
    candidate: unknown
  ): Promise<void> => {
    const track = asCameraTrack(candidate)
    if (!track || (track as { source?: Track.Source }).source !== Track.Source.Camera) return
    if (!wantsProcessing(effectRef.current)) return

    const processor = attachedProcessorOf(track)
    if (processor && (framesSeen.get(processor) ?? 0) > 0 && !holds.has(track)) {
      doneStarting(room)
      return
    }

    // The hold takes over from the start: one continuous "waiting" state.
    registerHold(room, track)
    doneStarting(room)
    // A track already marked paused would not pause again, but its new sender
    // was created carrying whatever the track holds — so clear it directly.
    if (track.isUpstreamPaused) await sender.replaceTrack(null)
    else await track.pauseUpstream?.()

    if (!processor) return
    await releaseOnFirstFrame(room, track, processor)
  }

  /**
   * Keep the effect on every newly published camera track.
   *
   * Hooked on the publish event rather than on each `setCameraEnabled` call
   * site: a camera track is published from the initial connect, a first camera
   * start, the RC3 auto-republish and a full reconnect (the same track again),
   * and one event covers them all. Camera off and on is a mute and an unmute of
   * the same track, and a device switch a restart; neither publishes.
   */
  const handleLocalTrackPublished = async (
    room: Room,
    publication: LocalTrackPublication
  ): Promise<void> => {
    // Screen share is a separate publication and must never be processed.
    if (publication.source !== Track.Source.Camera) return
    // The start is over; a hold, if one is needed, has taken over from it.
    doneStarting(room)

    const track = asCameraTrack(publication.track)
    const attached = track ? attachedProcessorOf(track) : null
    const current = processorRef.current
    const parked = parkedRef.current
    const effect = effectRef.current

    if (effect.kind === 'none') {
      // No effect wanted, but the track may still have come up with one: a
      // processor parked before the user picked None, started by a path that
      // read the capture defaults. Take it off, or the camera publishes
      // blurred with nothing left that would ever stop it.
      let rawAgain = false
      if (attached && track) {
        try {
          await track.stopProcessor()
          reattachVideoElements(track)
          rawAgain = true
        } catch {
          /* the track may already be gone */
        }
      }
      if (parked && parked.processor === attached) {
        parkedRef.current = null
        // Out of the defaults too, or every later camera start in this Room
        // starts that stripped processor again.
        const defaults = captureDefaultsOf(room)
        if (defaults.processor === attached) defaults.processor = undefined
      }
      processorRef.current = null
      frameStatsRef.current = []
      if (current) void destroyBackgroundProcessor(current)
      if (attached && attached !== current) void destroyBackgroundProcessor(attached)
      if (track) await release(track)
      // That track was started for an effect, so it is at the effect's capture;
      // so is one this module raised and could not put back (a restore the camera
      // refused, before a reconnect republished the same track). Last, so the
      // camera is on the wire first: this handler is not queued behind the apply
      // loop, and a camera that is slow to answer must not delay the raw picture
      // the user asked for.
      if (track && (rawAgain || raisedForEffect.has(track))) await restoreProfileCapture(track)
      return
    }

    if (!isBackgroundEffectAvailable()) {
      if (readCameraWithoutBackgroundOnThisDevice()) return
      // Started past the gate where the effect cannot run: never raw.
      if (track) await stopCamera(room, track)
      setProblem({ cause: 'unsupported', effect })
      return
    }

    // The same track, republished in place — a full reconnect republishes the
    // existing track objects, and LiveKit restarts their processor rather than
    // replacing it. It is still live and still ours: destroying it froze the
    // camera for everyone, and re-applying went through `setProcessor`, which
    // puts the raw camera on the wire before the new processor is ready.
    if (current && attached === current) return

    // The processor belonged to a track that has been replaced. **Destroy it
    // rather than only dropping the reference** — it owns a WebGL context, and
    // orphaning one per republish is what pushes the page toward the browser's
    // context ceiling.
    processorRef.current = null
    frameStatsRef.current = []
    if (current) void destroyBackgroundProcessor(current)

    if (attached) {
      // Attached from the capture defaults, inside createLocalTracks — before
      // this track was published, which is the point. Adopt it; building
      // another would mean two WebGL contexts for one camera. Usually it is
      // the one parked now. It can also be one taken out of the park after
      // LiveKit had already read it (a pick made while the camera was
      // starting): that one was not destroyed, because LiveKit was
      // initialising it, so it is ours to hold — and later destroy — from here.
      const wasParked = Boolean(
        parked && shouldAdoptCaptureTimeProcessor(attached, parked.processor)
      )
      processorRef.current = attached
      // Spent: LiveKit destroys a track's processor when the track stops, so
      // the next camera start needs a fresh one.
      if (wasParked) parkedRef.current = null
      // Publishing is not compositing: init() resolves once the pipeline is
      // wired, before a frame has been through segmentation. Hold the busy
      // state; the first composited frame clears it, and the cooldown is the
      // backstop if no frame ever arrives.
      beginVideoToggleCooldown()

      if (
        !wasParked ||
        parked.effectKey !== backgroundEffectKey(effect) ||
        isProcessorOnSteppedDownModel(attached)
      ) {
        // Started for an earlier choice. Switch it rather than rebuild it, unless
        // it was built before the page stepped down to the lighter model.
        await applyVideoBackground(room, effectRef.current, { persist: false })
        return
      }
      succeed(effect, parked.imageUrl ?? null)
      announceEffect(effect, false)
      // A toggle mutes and unmutes this track with its processor; the next
      // park waits until a new track can start (review #12).
      unparkWhileLive(room)
      return
    }

    // No processor: something started the camera past the gate. The sender
    // was held when it was created; keep it held and attach.
    if (track) hold(room, track)
    await applyVideoBackground(room, effectRef.current, { persist: false })
  }

  /**
   * A camera turned back on.
   *
   * With its processor, LiveKit has restarted the two together. If it was still
   * held for its first composited frame when it was turned off, that frame is
   * waited for now — the hold has kept it off the wire through the restart.
   *
   * Without one — for instance one whose effect failed and was turned back on
   * by a path the gate did not see — it is held and given its effect.
   */
  const handleCameraUnmuted = async (
    room: Room,
    publication: LocalTrackPublication
  ): Promise<void> => {
    if (publication.source !== Track.Source.Camera) return
    const track = asCameraTrack(publication.track)
    if (!track) return
    doneStarting(room)
    const attached = attachedProcessorOf(track)
    if (attached) {
      if (holds.has(track)) await releaseOnFirstFrame(room, track, attached)
      return
    }
    const effect = effectRef.current
    if (!wantsProcessing(effect)) return
    if (!isBackgroundEffectAvailable()) {
      await stopCamera(room, track)
      setProblem({ cause: 'unsupported', effect })
      return
    }
    hold(room, track)
    await applyVideoBackground(room, effect, { persist: false })
  }

  const handleCameraUnpublished = (room: Room, publication: LocalTrackPublication): void => {
    if (publication.source !== Track.Source.Camera) return
    const track = asCameraTrack(publication.track)
    // Otherwise a camera the system took away mid-hold leaves the self-view
    // covered after its replacement is up.
    if (track) forgetHold(track)
    // Review #12: a new camera track can start from here on (the RC3
    // republish), and nothing was parked while this one was live. Not for one
    // the lifecycle is taking down itself — its caller settles the park — nor
    // in a Room that is not connected: a full reconnect republishes this same
    // track, processor and all, and RC3 only restarts a camera in a connected
    // Room.
    if (track && stoppingCameras.has(track)) return
    // Decided a microtask later, not now: a disconnect (the server's, or ours
    // on a path that never retires the Room) unpublishes every track while the
    // Room still reads Connected, and LiveKit's `handleDisconnect` sets
    // Disconnected only in its `finally`, in the same synchronous call. Parking
    // now built a processor, and revalidated an image, for a Room that is
    // going away.
    void Promise.resolve()
      .then(() => {
        if ((room as { state?: unknown }).state !== ConnectionState.Connected) return undefined
        if (retiredRooms.has(room) || !wantsProcessing(effectRef.current)) return undefined
        if (!isBackgroundEffectAvailable() || readyParkFor(room, effectRef.current)) return undefined
        return parkWithImage(room, effectRef.current)
      })
      .catch((error: unknown) => {
        console.warn('[LiveKit] SC-158: could not park the background after an unpublish:', error)
      })
  }

  const retireRoom = (room: Room | null | undefined): void => {
    if (!room) return
    retiredRooms.add(room)
    doneStarting(room)
    // Review #8: what ran on this Room's camera is not running anywhere now.
    // Left set, a failed pick in the next Room fell back to it — "still
    // using" an effect that is not on, announced as a revert, camera taken down.
    liveEffect = NO_BACKGROUND_EFFECT
    liveImageUrl = null
    // An apply for this Room stops waiting — on a download, a processor start
    // or a first frame — so `whenSettled` settles and the next Room's camera
    // start is not kept behind it.
    if (latestRoom === room) {
      nextRequest.fire()
      nextRequest = deferredSignal()
    }
    // A camera start for this Room stops waiting on its processor too.
    for (const start of [...pendingStarts.values()]) {
      if (start.room === room) start.giveUp()
    }
    const parked = parkedRef.current
    if (parked?.room === room) {
      parkedRef.current = null
      const defaults = captureDefaultsOf(room)
      if (defaults.processor === parked.processor) defaults.processor = undefined
      // Whatever its state, including mid-init: nothing will render for a
      // retired Room, and a processor nobody holds never gives its WebGL
      // context back.
      discardProcessor(parked.processor)
    }
    for (const [track, held] of holds) {
      if (held.room === room) forgetHold(track)
    }
  }

  const forgetUser = (): void => {
    slowReported = false
    setProblem(null)
  }

  return {
    applyVideoBackground,
    parkCaptureProcessor,
    parkBeforeConnect,
    prepareCameraStart,
    handleCameraStartFailed,
    forgetCameraStart,
    detachVideoBackground,
    handleLocalSenderCreated,
    handleLocalTrackPublished,
    handleCameraUnmuted,
    handleCameraUnpublished,
    whenSettled,
    pickCount: () => pickCount,
    retireRoom,
    forgetUser,
  }
}
